import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import { closeForm } from "../feature/formSlice";
import { callAPI } from "../feature/APISlice";
import { showMessage } from "../feature/navbarSlice";
import { fetchLookups } from "../feature/lookupSlice";

const BASE_URL = `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/admin/team-members`;

const getDefaultFormData = () => ({
  name: "",
  designation: "",
  description: "",
  orderNumber: "",
  profileImage: "",
  is_active: false,
});

export default function TeamMemberForm() {
  const dispatch = useDispatch();

  const { isOpen, editingId } = useSelector((s) => s.form);
  const { current, loading, error } = useSelector((s) => s.api);
  const { media } = useSelector((s) => s.lookups);

  const [formData, setFormData] = useState(getDefaultFormData());

  
  useEffect(() => {
    if (isOpen) dispatch(fetchLookups());
  }, [isOpen, dispatch]);

  
  useEffect(() => {
    if (editingId) {
      dispatch(callAPI({ method: "get", url: `${BASE_URL}/${editingId}` }));
    }
  }, [editingId, dispatch]);

  
  useEffect(() => {
    if (current && editingId) {
      setFormData({
        name: current.name ?? "",
        designation: current.designation ?? "",
        description: current.description ?? "",
        orderNumber: current.orderNumber ?? "",
        profileImage: current.profileImage?.rivid ?? "",
        is_active: current.is_active ?? false,
      });
    }

    if (!editingId) setFormData(getDefaultFormData());
  }, [current, editingId]);

  
  const mediaOptions = useMemo(
    () =>
      media.map((m) => ({
        value: m.rivid,
        label: `RIVID: ${m.rivid}`,
      })),
    [media]
  );

  
  const handleChange = (name, value) =>
    setFormData((p) => ({ ...p, [name]: value }));

  const handleClose = () => {
    dispatch(closeForm());
    setFormData(getDefaultFormData());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingId ? "patch" : "post";
      const url = editingId ? `${BASE_URL}/${editingId}` : BASE_URL;

      await dispatch(callAPI({ method, url, data: formData })).unwrap();

      dispatch(
        showMessage({
          type: "success",
          text: `Team Member ${editingId ? "updated" : "created"} successfully`,
        })
      );

      handleClose();
    } catch (err) {
      dispatch(
        showMessage({
          type: "error",
          text: err || "Something went wrong",
        })
      );
    }
  };

  if (!isOpen) return null;

  
  return (
    <div className="admin-form-overlay">
      <div className="admin-form-wrapper" onClick={(e) => e.stopPropagation()}>
        <button className="admin-form-close" onClick={handleClose}>
          ✕
        </button>

        <h2>{editingId ? "Edit Team Member" : "Add Team Member"}</h2>

        <form className="admin-contact-form" onSubmit={handleSubmit}>
          
          <div className="admin-form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>

          
          <div className="admin-form-group">
            <label>Designation</label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) =>
                handleChange("designation", e.target.value)
              }
            />
          </div>

          
          <div className="admin-form-group">
            <label>Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) =>
                handleChange("description", e.target.value)
              }
            />
          </div>

          
          <div className="admin-form-group">
            <label>Order Number</label>
            <input
              type="text"
              value={formData.orderNumber}
              onChange={(e) =>
                handleChange("orderNumber", e.target.value)
              }
            />
          </div>

          
          <div className="admin-form-group">
            <label>Profile Image (Media RIVID)</label>
            <Select
              options={mediaOptions}
              value={
                mediaOptions.find(
                  (o) => o.value === Number(formData.profileImage)
                ) || null
              }
              onChange={(o) =>
                handleChange("profileImage", o?.value || "")
              }
              isClearable
            />
          </div>

          
          <div className="admin-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  handleChange("is_active", e.target.checked)
                }
              />{" "}
              Active
            </label>
          </div>

          
          <div className="admin-button-row">
            <button
              type="button"
              className="admin-btn outline"
              onClick={handleClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="admin-btn filled"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : editingId
                ? "Update"
                : "Save"}
            </button>
          </div>

          {error && <p className="error-text">{error}</p>}
        </form>
      </div>
    </div>
  );
}
